import { ESearchFilterKeys } from 'constants/SearchFilterTypes';
import { IFirearm } from 'models/firearm';
import { IAccessory, IAmmunition, IAncillary, IComponent } from 'models/ancillaries';
import {
  EAssetType,
  ILegalEntityAddressDetail,
  ILegalEntityAddressDetailAccessories,
  ILegalEntityAddressDetailAmmunition,
  ILegalEntityAddressDetailComponents,
  ILegalEntityAddressDetailFirearms,
  ILegalEntityAddressDetailLicence,
} from 'models/legalEntity';
import { ILicencesSearchForm } from 'models/licences';
import { IMapBounds } from 'models/map';
import { ILicenceConfigurationSearchFormValues } from 'pages/LicenceConfigurationPage/helper';

class SearchFilterStore {
  private static instance: SearchFilterStore | undefined;

  public _data: { [key: string]: any } = {};

  public _dictionaries: { [key: string]: any } = {};

  public _fireams: { data: IFirearm[] } = { data: [] };

  public _accessories: { data: IAccessory[] } = { data: [] };

  public _ammunition: { data: IAmmunition[] } = { data: [] };

  public _components: { data: IComponent[] } = { data: [] };

  public _ancillaries: { data: IAncillary[] } = { data: [] };

  public _licences: { data: ILicencesSearchForm[] } = { data: [] };

  public _licences_configuration: { data: ILicenceConfigurationSearchFormValues[] } = {
    data: [],
  };

  public _assets: { data: ILegalEntityAddressDetail } = {
    data: {
      firearms: [],
      accessories: [],
      ammunition: [],
      components: [],
      licences: [],
    },
  };

  public _mapBounds: { data: IMapBounds | undefined } = {
    data: undefined,
  };

  public _selectedAssetType: { data: EAssetType | null } = { data: null };

  constructor() {
    if (!SearchFilterStore.instance) {
      SearchFilterStore.instance = this;
    }
    // eslint-disable-next-line no-constructor-return
    return SearchFilterStore.instance;
  }

  setValue(key: ESearchFilterKeys, value: any) {
    this._data[key] = value;
  }

  getValue(key: ESearchFilterKeys) {
    return this._data?.[key] ? this._data[key] : null;
  }

  clearValue(key: ESearchFilterKeys) {
    this._data[key] = [];
  }

  setDictionaries(key: ESearchFilterKeys, value: any) {
    this._dictionaries[key] = value;
  }

  getDictionaries(key: ESearchFilterKeys) {
    return this._dictionaries?.[key] ? this._dictionaries[key] : null;
  }

  setSelectedAssetType(value: EAssetType | null) {
    this._selectedAssetType.data = value;
    const event = new CustomEvent('setAssetTypeEvent', {
      detail: { data: this._selectedAssetType.data },
    });
    document.dispatchEvent(event);
  }

  getSelectedAssetType() {
    return this._selectedAssetType.data;
  }

  setFirearms(firearms: IFirearm[] | ILegalEntityAddressDetailFirearms[]) {
    this._fireams.data = firearms as IFirearm[];
    this.setSelectedAssetType(firearms.length ? EAssetType.firearms : null);
    this._ammunition.data = [];
    this._components.data = [];
    this._accessories.data = [];
    this._licences.data = [];

    const event = new CustomEvent('setFirearmsEvent', {
      detail: { data: this._fireams.data },
    });
    document.dispatchEvent(event);
  }

  getFirearms() {
    return this._fireams.data;
  }

  setAccessories(accessories: IAccessory[] | ILegalEntityAddressDetailAccessories[]) {
    this._accessories.data = accessories as IAccessory[];
    this.setSelectedAssetType(accessories.length ? EAssetType.accessories : null);
    this._ammunition.data = [];
    this._components.data = [];
    this._fireams.data = [];
    this._licences.data = [];

    const event = new CustomEvent('setAccessoriesEvent', {
      detail: { data: this._accessories.data },
    });
    document.dispatchEvent(event);
  }

  setComponents(components: IComponent[] | ILegalEntityAddressDetailComponents[]) {
    this._components.data = components as IComponent[];
    this.setSelectedAssetType(components.length ? EAssetType.components : null);
    this._ammunition.data = [];
    this._accessories.data = [];
    this._fireams.data = [];
    this._licences.data = [];

    const event = new CustomEvent('setComponentsEvent', {
      detail: { data: this._components.data },
    });
    document.dispatchEvent(event);
  }

  setAmmunition(ammunitions: IAmmunition[] | ILegalEntityAddressDetailAmmunition[]) {
    this._ammunition.data = ammunitions as IAmmunition[];
    this.setSelectedAssetType(ammunitions.length ? EAssetType.ammunition : null);
    this._components.data = [];
    this._accessories.data = [];
    this._fireams.data = [];
    this._licences.data = [];

    const event = new CustomEvent('setAmmunitionsEvent', {
      detail: { data: this._ammunition.data },
    });
    document.dispatchEvent(event);
  }

  setAncillaries(ancillaries: IAncillary[]) {
    this._ancillaries.data = ancillaries;
    const event = new CustomEvent('setAncillariesEvent', {
      detail: { data: this._ancillaries.data },
    });
    document.dispatchEvent(event);
  }

  setAssets(assets: ILegalEntityAddressDetail | null) {
    this._assets.data = assets ?? {
      firearms: [],
      accessories: [],
      ammunition: [],
      components: [],
      licences: [],
    };
    const event = new CustomEvent('setAssetsEvent', {
      detail: { data: this._assets.data },
    });
    document.dispatchEvent(event);
  }

  setMapBounds(mapBounds: IMapBounds | undefined) {
    this._mapBounds.data = mapBounds;
  }

  clearAll() {
    this._ancillaries.data = [];
    this._ammunition.data = [];
    this._licences.data = [];
    this._components.data = [];
    this._accessories.data = [];
    this._fireams.data = [];
    this._selectedAssetType.data = null;
    this._mapBounds.data = undefined;
  }

  getAccessories() {
    return this._accessories.data;
  }

  getAmmunition() {
    return this._ammunition.data;
  }

  getComponents() {
    return this._components.data;
  }

  setLicences(licences: ILicencesSearchForm[] | ILegalEntityAddressDetailLicence[]) {
    this._licences.data = licences as ILicencesSearchForm[];
    this.setSelectedAssetType(licences.length ? EAssetType.licences : null);
    this._ammunition.data = [];
    this._accessories.data = [];
    this._components.data = [];
    this._fireams.data = [];

    const event = new CustomEvent('setLicencesEvent', {
      detail: { data: this._licences.data },
    });
    document.dispatchEvent(event);
  }

  getLicences() {
    return this._licences.data;
  }

  getMapBounds() {
    return this._mapBounds.data;
  }

  setLicencesConfiguration(licences: ILicenceConfigurationSearchFormValues[]) {
    this._licences_configuration.data = licences as ILicenceConfigurationSearchFormValues[];
    this.setSelectedAssetType(licences.length ? EAssetType.licences : null);
    this._licences.data = [];
    this._ammunition.data = [];
    this._accessories.data = [];
    this._components.data = [];
    this._fireams.data = [];

    const event = new CustomEvent('setLicencesConfigurationEvent', {
      detail: { data: this._licences_configuration.data },
    });
    document.dispatchEvent(event);
  }

  getLicencesConfiguration() {
    return this._licences_configuration.data;
  }
}

const instance = new SearchFilterStore();
Object.freeze(instance);

export default instance;
